import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AUTH } from "@/constants/testIds";
import { LanguageSwitch } from "@/lib/i18n";
import { Sprout, BookOpen, GraduationCap, ScrollText, Users } from "lucide-react";

const BG_IMAGE =
  "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&auto=format&fit=crop&w=1800&q=80";

function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email.trim(), password.trim());
      const dest = u.role === "studente" ? "/portale" : u.role === "docente" ? "/docente" : u.role === "economato" ? "/wallet" : "/";
      navigate(dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5 bg-[#FDFBF7]">
      {/* Hero — scuola missionaria */}
      <div className="hidden lg:block lg:col-span-3 relative overflow-hidden">
        <img src={BG_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0F241D]/95 via-[#1C352D]/92 to-[#1C352D]/85" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F241D]/70 via-transparent to-transparent" />
        {/* subtle noise / grain */}
        <div className="absolute inset-0 opacity-[0.08] mix-blend-overlay pointer-events-none"
             style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }}/>

        <div className="relative h-full flex flex-col justify-between p-14 text-stone-50">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#E2725B] grid place-items-center shadow-lg shadow-[#E2725B]/20">
              <Sprout size={22} />
            </div>
            <div>
              <div className="font-semibold tracking-tight text-lg">RDM ClassRoom</div>
              <div className="text-[10px] text-[#E2b8a8] uppercase tracking-[0.3em] mt-0.5">Ramo di Mandorlo · Scuola Missionaria</div>
            </div>
          </div>

          <div className="max-w-xl">
            <div className="inline-block text-[10px] uppercase tracking-[0.4em] text-[#E2725B] mb-6 font-semibold">
              Geremia 1,11 — «Vedo un ramo di mandorlo»
            </div>
            <h1 className="text-5xl xl:text-6xl font-light tracking-tight leading-[1.05]">
              Formare chi <span className="italic">porta</span> <br />
              <span className="text-[#E2725B] font-semibold not-italic">il seme della Parola.</span>
            </h1>
            <p className="mt-7 text-stone-200/90 text-lg leading-relaxed max-w-lg">
              La piattaforma della segreteria didattica, dei docenti e degli studenti del <b>Ramo di Mandorlo</b>:
              piani di studio, presenze, esami, comunicazioni e attestati — tutto in un unico luogo.
            </p>
          </div>

          <div className="grid grid-cols-4 gap-4 max-w-2xl">
            <FeaturePill icon={GraduationCap} label="Studenti" />
            <FeaturePill icon={BookOpen} label="Corsi & CFU" />
            <FeaturePill icon={ScrollText} label="Attestati" />
            <FeaturePill icon={Users} label="Segreteria" />
          </div>
        </div>
      </div>

      {/* Form login */}
      <div className="lg:col-span-2 flex items-center justify-center px-8 py-16 relative">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10 flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[#1C352D] text-white grid place-items-center">
              <Sprout size={22} />
            </div>
            <div>
              <div className="font-semibold text-[#1C352D]">RDM ClassRoom</div>
              <div className="text-[10px] text-stone-500 uppercase tracking-[0.25em]">Ramo di Mandorlo · Scuola</div>
            </div>
          </div>

          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.35em] text-[#E2725B] font-semibold">Portale unico</div>
            <LanguageSwitch withEs />
          </div>
          <h2 className="text-3xl font-semibold tracking-tight text-[#1C352D] leading-tight">Accedi<br/>alla tua scuola.</h2>
          <p className="text-sm text-stone-500 mt-3 leading-relaxed">
            Segreteria, docenti e studenti condividono lo stesso spazio.<br/>
            Inserisci le credenziali per continuare.
          </p>

          <form onSubmit={onSubmit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Email</Label>
              <Input
                id="email" type="email" required
                data-testid={AUTH.loginEmail}
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-white h-11 border-stone-200 focus-visible:ring-[#1C352D]"
                placeholder="tuo@indirizzo.it"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.25em] text-stone-500">Password</Label>
              <Input
                id="password" type="password" required
                data-testid={AUTH.loginPassword}
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-white h-11 border-stone-200 focus-visible:ring-[#1C352D]"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <div data-testid={AUTH.loginError} className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" data-testid={AUTH.loginSubmit}
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#1C352D] hover:bg-[#264A3E] transition-colors duration-150 text-[15px]"
            >
              {loading ? "Accesso in corso…" : "Entra nel portale"}
            </Button>
            <div className="text-right">
              <a href="/forgot-password" data-testid="forgot-password-link" className="text-[12px] text-stone-500 hover:text-[#E2725B] underline underline-offset-4">
                Password dimenticata?
              </a>
            </div>
          </form>

          <div className="mt-8 pt-6 border-t border-stone-100 text-[11px] text-stone-500 leading-relaxed">
            <b className="text-stone-700">Sei uno studente?</b> Se hai ricevuto una email di attivazione, usa
            quelle credenziali. Al primo accesso ti verrà chiesto di scegliere una password personale.
          </div>

          <p className="mt-10 text-[11px] text-stone-400">
            © {new Date().getFullYear()} Ramo di Mandorlo · RDM ClassRoom
          </p>
        </div>
      </div>
    </div>
  );
}

function FeaturePill({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-start gap-2 p-3 rounded-lg bg-white/5 backdrop-blur-sm border border-white/10">
      <div className="w-7 h-7 rounded-md bg-white/10 grid place-items-center text-[#E2b8a8]">
        <Icon size={14} strokeWidth={1.75}/>
      </div>
      <span className="text-[11px] uppercase tracking-widest text-stone-200/80">{label}</span>
    </div>
  );
}
